<template>
  <div class="container-fluid basic-background">
    <div class="row">
      <!-- 左側：上傳與標記區域 -->
      <div class="col-lg-7 mb-4 order-2 order-lg-1">
        <div class="block-card py-4 d-flex flex-column">
          <h1 class="h4 text-left letter-2px mx-4 mb-4">
            {{ $t("imageInpainting") }}
          </h1>

          <!-- 上傳圖片方框 (支援拖曳) -->
          <div
            class="upload-box mx-4"
            :class="{ 'drag-over': isDragging }"
            @dragover.prevent="onDragOver"
            @dragleave="onDragLeave"
            @drop.prevent="onDrop"
          >
            <div v-if="uploadedImage" class="image-preview">
              <!-- Canvas 用來呈現圖片並進行筆刷標記 -->
              <canvas
                ref="canvas"
                :key="uploadedImage"
                class="fixed-preview"
                @mousedown="handleMouseDown"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
                @mouseleave="handleMouseUp"
              />
              <!-- 右上角按鈕容器 -->
              <div class="top-right-buttons">
                <!-- 清除標記按鈕 -->
                <button class="clear-mask-btn" :title="$t('clearMark')" @click="resetMask">
                  <i class="bi bi-eraser" />
                </button>
                <!-- 清除圖片按鈕 -->
                <button class="close-btn" :title="$t('clearImage')" @click="clearImage">
                  ×
                </button>
              </div>
            </div>

            <div v-else class="upload-placeholder">
              <p class="d-none d-lg-block">{{ $t("dragDropUpload") }}</p>
              <p class="d-none d-lg-block">— {{ $t("or") }} —</p>
              <label class="btn block-btn block-btn-hover">
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  @change="handleFileUpload"
                >
                <i class="bi bi-upload upload-icon"></i>
                {{ $t("clickToUploadImage") }}
              </label>
            </div>
          </div>

          <!-- 參數設定 -->
          <div class="mt-4">
            <!-- 筆刷大小設定 - 可收合 -->
            <div class="collapsible-section">
              <div class="section-header" @click="toggleSection('brushSize')">
                <i :class="['bi', showSections.brushSize ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
                <span class="section-title">{{ $t("brushSize") }}: {{ brushSize }}</span>
              </div>
              <div v-show="showSections.brushSize" class="section-content">
                <label for="brush-size" class="visually-hidden">{{ $t("brushSize") }}</label>
                <input
                  id="brush-size"
                  v-model.number="brushSize"
                  type="range"
                  class="form-range dynamic-progress-slider"
                  min="5"
                  max="50"
                  step="1"
                >
              </div>
            </div>

            <!-- Seed 欄位 - 可收合 -->
            <div class="collapsible-section">
              <div class="section-header" @click="toggleSection('seed')">
                <i :class="['bi', showSections.seed ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
                <span class="section-title">
                  <i 
                    ref="seedTooltip"
                    class="info-icon bi bi-info-circle me-2"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    :data-bs-original-title="$t('seedInfo')"
                  />
                  {{ $t("seed") }}
                </span>
              </div>
              <div v-show="showSections.seed" class="section-content">
                <div class="seed-group">
                  <label for="seed-inpainting" class="visually-hidden">{{ $t("seed") }}</label>
                  <input
                    id="seed-inpainting"
                    v-model="seed"
                    type="number"
                    class="form-control seed-group-input"
                    :placeholder="$t('enterSeedValue')"
                  >
                  <button class="btn seed-group-button" :aria-label="$t('getRandomSeed')" @click="generateRandomSeed">
                    <i
                      ref="diceTooltip"
                      class="bi bi-dice-3-fill"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      :data-bs-original-title="$t('getRandomSeed')"
                    />
                  </button>
                </div>
              </div>
            </div>

            <!-- Guidance 欄位 - 可收合 -->
            <div class="collapsible-section">
              <div class="section-header" @click="toggleSection('guidance')">
                <i :class="['bi', showSections.guidance ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
                <span class="section-title">
                  <i 
                    ref="guidanceTooltip"
                    class="info-icon bi bi-info-circle me-2"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    :data-bs-original-title="$t('guidanceInfo')"
                  />
                  {{ $t("guidance") }}: {{ guidance }}
                </span>
              </div>
              <div v-show="showSections.guidance" class="section-content">
                <label for="guidance-range" class="visually-hidden">{{ $t("guidance") }}</label>
                <input
                  id="guidance-range"
                  v-model.number="guidance"
                  type="range"
                  class="form-range dynamic-progress-slider"
                  min="1"
                  max="30"
                  step="0.1"
                >
              </div>
            </div>

            <!-- Prompt 欄位 -->
            <div class="mt-3 px-4">
              <label for="prompt-text" class="form-label">{{ $t("prompt") }}:</label>
              <textarea
                id="prompt-text"
                v-model="positivePrompt"
                class="form-control"
                rows="4"
                @input="updatePrompt"
              />
            </div>

            <!-- 送出請求 -->
            <div class="d-flex justify-content-center">
              <button class="btn block-btn block-btn-hover mt-4" :disabled="!uploadedImage || isLoading" @click="submitInpainting">
                <span v-if="isLoading">
                  {{ $t("inpaintImage") }}...
                </span>
                <span v-else>
                  {{ uploadedImage ? $t("inpaintImage") : $t("pleaseUploadImage") }}
                </span>
              </button>
            </div>
            
          </div>
        </div>
      </div>

      <!-- 右側：處理後的圖片呈現 -->
      <div class="col-lg-5 mb-4 order-1 order-lg-2">
        <div class="block-card px-4 py-4 d-flex flex-column">
          <h4 class="text-left letter-2px mb-4">
            {{ $t("inpaintImage") }}
          </h4>
          <div class="result-display-container">
            <div v-if="isLoading" class="loading-container">
              <div class="loading-spinner">
                <div class="gradient-circle"></div>
                <div class="loading-text">
                  {{ $t("generatingYouriImage") }}
                </div>
                <div class="loading-tips">
                  {{ $t("inpaintTips") }}
                </div>
              </div>
            </div>
            <div v-else-if="processedImage" class="result-image-container">
              <img :src="processedImage" class="result-image" alt="Processed result">
              <div class="download-overlay">
                <button class="btn block-btn" @click="downloadImage">
                  {{ $t("downloadImage") }}
                </button>
              </div>
            </div>
            <div v-else class="no-result-placeholder">
              <p class="no-image-text">
                {{ $t("noImageInpainting") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from 'bootstrap/js/dist/tooltip';
import config from '@/config.json'
import api from '@/api/axios';

export default {
  name: "ImageInpainting",
  data() {
    return {
      uploadedImage: null,    // 上傳圖片的 URL
      processedImage: null,   // 後端處理後圖片的 URL
      width: 512,             // 參數用的寬度
      height: 512,            // 參數用的高度
      guidance: 3.5,
      brushSize: 30,          // 筆刷半徑
      isDrawing: false,       // 是否正在繪製筆刷
      isDragging: false,      // 拖曳上傳提示用
      isLoading: false,       // 等待 API 回應時顯示 spinner
      originalImage: null,    // 儲存上傳後的 Image 物件
      positivePrompt: "A taiwanese young woman walking on the street, waering jacket. Brick building in the background and few people on the street. In 1930 Taiwan street.",
      steps: 28,
      seed: "492147161085354",
      // 預覽用 canvas 尺寸與限制
      canvasWidth: 0,
      canvasHeight: 0,
      maxCanvasWidth: 560,    // 預覽區最大寬度
      maxCanvasHeight: 560,   // 預覽區最大高度
      tooltips: [],
      // 控制各個區段的展開/收合狀態
      showSections: {
        brushSize: true,
        seed: false,
        guidance: false
      }
    };
  },
  
  watch: {
    "$i18n.locale": function () {
      this.refreshTooltips();
    },
  },
  
  async mounted() {
    this.tooltips = [];
    this.initTooltips();
  },
  
  methods: {
    // 新增：切換區段展開/收合狀態
    toggleSection(sectionName) {
      this.showSections[sectionName] = !this.showSections[sectionName];
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      this.loadImage(file);
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.loadImage(file);
    },
    loadImage(file) {
      this.clearImage();       // 清除舊圖片
      if (file && file.type.startsWith("image/")) {
        this.uploadedImage = URL.createObjectURL(file);
        this.originalImage = new Image();
        this.originalImage.src = this.uploadedImage;
        this.originalImage.onload = () => {
          this.setupCanvas();
        };
      }
    },

    setupCanvas() {
      const canvas = this.$refs.canvas;
      if (canvas && this.originalImage) {
        const container = canvas.parentElement;
        const containerWidth = container.offsetWidth;
        
        const naturalWidth = this.originalImage.naturalWidth;
        const naturalHeight = this.originalImage.naturalHeight;
        
        // 寬度直接等於容器寬度
        this.canvasWidth = containerWidth;
        // 高度按比例計算
        this.canvasHeight = (naturalHeight / naturalWidth) * containerWidth;

        canvas.width = this.canvasWidth;
        canvas.height = this.canvasHeight;
        canvas.style.width = this.canvasWidth + 'px';
        canvas.style.height = this.canvasHeight + 'px';
        
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(this.originalImage, 0, 0, canvas.width, canvas.height);
      }
    },
    clearImage() {
      this.uploadedImage = null;
      this.originalImage = null;
      this.canvasWidth = 0;
      this.canvasHeight = 0;
      this.processedImage = null; // 清除結果圖片
      const canvas = this.$refs.canvas;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    },
    resetMask() {
      if (this.originalImage) {
        this.setupCanvas();
      }
    },
    handleMouseDown(event) {
      this.isDrawing = true;
      this.draw(event);
    },
    handleMouseMove(event) {
      if (!this.isDrawing) return;
      this.draw(event);
    },
    handleMouseUp() {
      this.isDrawing = false;
    },
    draw(event) {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      // 呼叫 getBoundingClientRect() 方法取得 canvas 的邊界資訊（包括位置與尺寸），這個物件包含了 canvas 相對於瀏覽器視窗的偏移量。
      const rect = canvas.getBoundingClientRect();
      // 計算縮放比例：canvas 實際像素尺寸 vs 顯示尺寸
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      // 根據滑鼠事件的 clientX 與 clientY（滑鼠在瀏覽器視窗中的座標），減去 canvas 的左上角位置（rect.left 與 rect.top），
      // 然後乘以縮放比例，計算出在 canvas 實際像素座標系中的位置 (x, y)。
      const x = (event.clientX - rect.left) * scaleX;
      const y = (event.clientY - rect.top) * scaleY;
      // 取得 canvas 的 2D 繪圖上下文 (context)，這是操作 canvas 畫圖的主要接口。
      const ctx = canvas.getContext("2d");
      // 設定全局合成模式 (globalCompositeOperation) 為 "destination-out"。這個模式下，繪製的內容會「扣除」已存在圖像的像素，
      // 使其變透明。這正是用來實現「擦除」或「標記」效果的關鍵。
      ctx.globalCompositeOperation = "destination-out";
      // 使用 createRadialGradient 方法建立一個輻射狀（圓形）漸層，漸層的中心點為 (x, y)：
      // 第一組參數：圓心位置 (x, y) 與內圈半徑 0，表示中心點。
      // 第二組參數：同樣中心 (x, y) 與外圈半徑 this.brushSize，定義漸層的範圍。
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, this.brushSize);
      // 這裡我們設定一個非常小的中央區域(0～0.05)是全透明(完全抹除)，
      // 然後從 0.1 到 1 平滑過渡到完全不抹除
      gradient.addColorStop(0, "rgba(0,0,0,0.8)");      // 中心點：完全抹除
      gradient.addColorStop(0.05, "rgba(0,0,0,0.8)");    // 內部5%仍然完全抹除
      gradient.addColorStop(0.8, "rgba(0,0,0,0.1)");  // 80%部分開始平滑過渡, 且只有10%的抹除效果
      gradient.addColorStop(1, "rgba(0,0,0,0)");      // 邊緣：完全透明(不抹除保持原圖)
      // 將 fillStyle 設定為剛剛創建的漸層，接下來所有填充操作都會使用這個漸層作為顏色。
      ctx.fillStyle = gradient;
      // 使用 blur filter 來使筆刷邊緣更柔和
      ctx.filter = "blur(3px)";  // 你可以調整這個數值使邊緣更柔
      // 開始一個新的路徑（path），這是為了畫出筆刷形狀，確保不受之前路徑影響。
      ctx.beginPath();
      // 以 (x, y) 為中心，並以 this.brushSize 作為半徑，畫一個完整的圓形（從 0 到 2π 弧度）。這個圓形代表一次筆刷擦除的範圍。
      ctx.arc(x, y, this.brushSize, 0, Math.PI * 2);
      // 使用之前設定的漸層填充剛剛建立的圓形。由於合成模式是 "destination-out"，這個動作會讓圓形區域的圖像變透明，形成標記效果。
      ctx.fill();
      // 恢復預設 filter 與合成模式
      ctx.filter = "none";
      // 將合成模式重設回預設值 "source-over"。這樣後續的繪圖操作就不會受 "destination-out" 模式的影響，恢復正常的繪製行為。
      ctx.globalCompositeOperation = "source-over";
    },
    submitInpainting() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      this.isLoading = true;
      setTimeout(() => {
        canvas.toBlob(async (blob) => {
          if (!blob) {
            console.error("Canvas blob 產生失敗");
            this.isLoading = false;
            return;
          }
          try {
            let formData = new FormData();
            formData.append("image", blob, "masked-" + Date.now() + ".png");
            formData.append("positive_prompt", this.positivePrompt);
            formData.append("width", this.width);
            formData.append("height", this.height);
            formData.append("guidance", this.guidance);
            formData.append("steps", this.steps);
            formData.append("seed", this.seed);

            const response = await api.post(
              `${config.apiBaseUrl}/stable_diffusion/api/inpaint`,
              formData,
              { responseType: "blob" }
            );
            this.processedImage = URL.createObjectURL(response.data);
            this.isLoading = false;
          } catch (error) {
            console.error("Error:", error);
            this.isLoading = false;
          }
        }, "image/png");
      }, 100); // 延遲100ms
    },
    generateRandomSeed() {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      this.seed = array[0].toString();
    },
    initTooltips() {
      this.destroyTooltips();
      this.$nextTick(() => {
        this.tooltips = [];
        const tooltipElements = [
          this.$refs.seedTooltip,
          this.$refs.diceTooltip,
          this.$refs.guidanceTooltip
        ];
        tooltipElements.forEach((element) => {
          if (element) {
            const tooltipInstance = new Tooltip(element, {
              trigger: "hover focus",
              placement: "top",
              title: element.getAttribute("data-bs-original-title"),
            });
            this.tooltips.push(tooltipInstance);
          }
        });
      });
    },
    destroyTooltips() {
      if (Array.isArray(this.tooltips)) {
        this.tooltips.forEach((tooltip) => tooltip.dispose());
        this.tooltips = [];
      }
    },
    refreshTooltips() {
      this.initTooltips();
    },
    /** 下載圖片 */
    downloadImage() {
      if (this.processedImage) {
        const link = document.createElement('a');
        link.href = this.processedImage;
        link.download = `inpainted_image_${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  },
};
</script>

<style scoped>
/* 原有樣式保持不變 */
.upload-box { border-radius: 15px; padding: 20px; text-align: center; transition: all 0.3s ease; background-color: #f8f9fa; min-height: 400px; display: flex; align-items: center; justify-content: center; }
.upload-box:hover { border-color: #bbb; background-color: #f0f0f0; }
.upload-box.drag-over { border-color: #007bff; background-color: #e3f2fd; }
.upload-placeholder p { margin: 10px 0; color: #6c757d; font-size: 1rem; }
.upload-icon { color: #fff; font-size: 16px; }
.image-preview { position: relative; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.fixed-preview { box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); cursor: crosshair; }
canvas.fixed-preview { box-shadow: none; }
.top-right-buttons { position: absolute; top: -10px; right: -10px; display: flex; flex-direction: row; gap: 5px; }
.close-btn { width: 30px; height: 30px; background: #000; color: white; border: none; border-radius: 50%; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); }
.close-btn:hover { background: #333; }
.clear-mask-btn { width: 30px; height: 30px; background: #000; color: white; border: none; border-radius: 50%; font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); }
.clear-mask-btn:hover { background: #333; }

/* 輸入欄位樣式 */
.input-with-tooltip { display: flex; align-items: center; gap: 8px; }

/* 按鈕樣式覆寫 */
.upscale-btn { display: block; margin: 0 auto; }
.upscale-btn:disabled { background-color: #6c757d !important; border-color: #6c757d !important; cursor: not-allowed; opacity: 0.6; }
.upscale-btn:not(:disabled):hover { background-color: #333 !important; border-color: #333 !important; transform: translateY(-1px); }

/* Info icon 樣式 */
.info-icon {
  color: #6c757d;
  font-size: 0.9rem;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .upload-box { min-height: 200px; padding: 15px; }
}
</style>